import {
  getLocationLogPayload,
  getNcrStatusPayload,
  buildFieldScreenshotDataFromScreenshots,
  extractLocationFromDeviceInfo,
  normalizeLocationHistory,
  LOCATION_HISTORY_MAX_RECORDS,
} from "./mdmDeviceDisplayUtils";
import { isSuccessfulCommandResult, normalizeDeviceResult } from "./mdmCommandUtils";

const appendLocationHistoryEntry = (existingHistory, deviceInfo, fetchedAt = null) => {
  const parsedLocation = extractLocationFromDeviceInfo(deviceInfo);
  if (!parsedLocation) return normalizeLocationHistory(existingHistory);

  const lat = Number(parsedLocation.locationLatitude);
  const lon = Number(parsedLocation.locationLongitude);
  if (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat === 0 &&
    lon === 0
  ) {
    return normalizeLocationHistory(existingHistory);
  }

  const entry = {
    locationLatitude: parsedLocation.locationLatitude,
    locationLongitude: parsedLocation.locationLongitude,
    updated_at: fetchedAt || new Date().toISOString(),
  };
  if (deviceInfo?.timestamp != null && deviceInfo.timestamp !== "") {
    entry.timestamp = deviceInfo.timestamp;
  }

  const history = normalizeLocationHistory(existingHistory);
  history.push(entry);
  if (history.length > LOCATION_HISTORY_MAX_RECORDS) {
    return history.slice(-LOCATION_HISTORY_MAX_RECORDS);
  }
  return history;
};

export const findDevicePath = (source, targetDeviceId) => {
  if (!source || !targetDeviceId) return null;
  const retailers = Object.entries(source);
  for (let i = 0; i < retailers.length; i += 1) {
    const [retailerName, branchesObj] = retailers[i];
    const branches = Object.entries(branchesObj || {});
    for (let j = 0; j < branches.length; j += 1) {
      const [branchName, devicesObj] = branches[j];
      if (devicesObj && Object.prototype.hasOwnProperty.call(devicesObj, targetDeviceId)) {
        return { retailerName, branchName };
      }
    }
  }
  return null;
};

export const mergeDeviceResultsIntoCache = (source, results) => {
  const nextDevices = JSON.parse(JSON.stringify(source || {}));
  let updatedCount = 0;
  const seen = new Set();

  results.forEach((result) => {
    const normalized = normalizeDeviceResult(result);
    const deviceId = normalized.deviceId;
    if (!deviceId || seen.has(deviceId)) return;
    seen.add(deviceId);

    const deviceInfo =
      normalized?.normalizedResult?.device_info ||
      normalized?.normalizedResult?.deviceInfo ||
      normalized?.normalizedResult?.payload?.device_info ||
      normalized?.normalizedResult?.data?.device_info ||
      (normalized?.normalizedResult?.data &&
      typeof normalized.normalizedResult.data === "object" &&
      !Array.isArray(normalized.normalizedResult.data)
        ? normalized.normalizedResult.data
        : null);

    const resolvedPath = findDevicePath(nextDevices, deviceId);
    if (resolvedPath) {
      const existingPayload =
        nextDevices?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[deviceId] || {};
      const resultPayload =
        normalized?.normalizedResult?.device_payload ||
        normalized?.normalizedResult?.devicePayload ||
        {};
      const nextDeviceInfo = deviceInfo || existingPayload?.device_info || null;
      const fetchedAt =
        normalized?.normalizedResult?.updated_at ||
        normalized?.normalizedResult?.data?.updated_at ||
        null;
      const shouldAppendHistory =
        Boolean(deviceInfo) && isSuccessfulCommandResult(normalized.normalizedResult);
      nextDevices[resolvedPath.retailerName][resolvedPath.branchName][deviceId] = {
        ...existingPayload,
        ...resultPayload,
        is_connected:
          typeof normalized?.normalizedResult?.is_connected === "boolean"
            ? normalized.normalizedResult.is_connected
            : existingPayload?.is_connected,
        device_info: nextDeviceInfo,
        location_history: shouldAppendHistory
          ? appendLocationHistoryEntry(
              existingPayload?.location_history,
              deviceInfo,
              fetchedAt
            )
          : existingPayload?.location_history || [],
      };
      updatedCount += 1;
      return;
    }

    if (!deviceInfo) return;

    const retailerName =
      normalized?.normalizedResult?.retailer?.name ||
      normalized?.normalizedResult?.retailer_name ||
      normalized?.normalizedResult?.retailerName ||
      "Unknown Retailer";
    const branchName =
      normalized?.normalizedResult?.branch?.name ||
      normalized?.normalizedResult?.branch_name ||
      normalized?.normalizedResult?.branchName ||
      "Unknown Branch";

    if (!nextDevices[retailerName]) nextDevices[retailerName] = {};
    if (!nextDevices[retailerName][branchName]) nextDevices[retailerName][branchName] = {};
    const fetchedAt =
      normalized?.normalizedResult?.updated_at ||
      normalized?.normalizedResult?.data?.updated_at ||
      null;
    const shouldAppendHistory = isSuccessfulCommandResult(normalized.normalizedResult);
    nextDevices[retailerName][branchName][deviceId] = {
      retailer: { name: retailerName },
      branch: { name: branchName },
      is_connected:
        typeof normalized?.normalizedResult?.is_connected === "boolean"
          ? normalized.normalizedResult.is_connected
          : null,
      device_info: deviceInfo,
      location_history: shouldAppendHistory
        ? appendLocationHistoryEntry([], deviceInfo, fetchedAt)
        : [],
    };
    updatedCount += 1;
  });

  return { nextDevices, updatedCount };
};

export const mergeLocationLogResultsIntoCache = (source, results) => {
  const nextDevices = JSON.parse(JSON.stringify(source || {}));
  let updatedCount = 0;
  const seen = new Set();

  results.forEach((result) => {
    const normalized = normalizeDeviceResult(result);
    const deviceId = normalized.deviceId;
    if (!deviceId || seen.has(deviceId)) return;
    if (!isSuccessfulCommandResult(normalized.normalizedResult)) return;

    const locationLog = getLocationLogPayload(normalized.normalizedResult);
    if (!locationLog || typeof locationLog !== "object") return;

    const resolvedPath = findDevicePath(nextDevices, deviceId);
    if (!resolvedPath) return;
    seen.add(deviceId);

    const existingPayload =
      nextDevices?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[deviceId] || {};
    nextDevices[resolvedPath.retailerName][resolvedPath.branchName][deviceId] = {
      ...existingPayload,
      location_log: locationLog,
    };
    updatedCount += 1;
  });

  return { nextDevices, updatedCount };
};

export const mergeNcrStatusResultsIntoCache = (source, results) => {
  const nextDevices = JSON.parse(JSON.stringify(source || {}));
  let updatedCount = 0;
  const seen = new Set();

  results.forEach((result) => {
    const normalized = normalizeDeviceResult(result);
    const deviceId = normalized.deviceId;
    if (!deviceId || seen.has(deviceId)) return;
    if (!isSuccessfulCommandResult(normalized.normalizedResult)) return;

    const ncrStatus = getNcrStatusPayload(normalized.normalizedResult);
    if (!ncrStatus || typeof ncrStatus !== "object") return;

    const resolvedPath = findDevicePath(nextDevices, deviceId);
    if (!resolvedPath) return;
    seen.add(deviceId);

    const existingPayload =
      nextDevices?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[deviceId] || {};
    nextDevices[resolvedPath.retailerName][resolvedPath.branchName][deviceId] = {
      ...existingPayload,
      ncr_status: ncrStatus,
    };
    updatedCount += 1;
  });

  return { nextDevices, updatedCount };
};

export const mergeFieldScreenshotResultsIntoCache = (source, results) => {
  const nextDevices = JSON.parse(JSON.stringify(source || {}));
  let updatedCount = 0;
  const seen = new Set();

  (Array.isArray(results) ? results : []).forEach((result) => {
    const deviceId = String(
      result?.device_id || result?.deviceId || result?.serial_number || result?.serialNumber || ""
    ).trim();
    if (!deviceId || seen.has(deviceId)) return;

    const fieldScreenshot = buildFieldScreenshotDataFromScreenshots(result?.screenshots);
    if (!fieldScreenshot) return;

    const resolvedPath = findDevicePath(nextDevices, deviceId);
    if (!resolvedPath) return;
    seen.add(deviceId);

    const existingPayload =
      nextDevices?.[resolvedPath.retailerName]?.[resolvedPath.branchName]?.[deviceId] || {};
    nextDevices[resolvedPath.retailerName][resolvedPath.branchName][deviceId] = {
      ...existingPayload,
      field_screenshot: fieldScreenshot,
    };
    updatedCount += 1;
  });

  return { nextDevices, updatedCount };
};

export const patchDeviceNotesInCache = (source, deviceId, notes) => {
  const nextDevices = JSON.parse(JSON.stringify(source || {}));
  let updatedCount = 0;

  Object.entries(nextDevices).forEach(([retailerName, branchesObj]) => {
    Object.entries(branchesObj || {}).forEach(([branchName, devicesObj]) => {
      if (devicesObj && Object.prototype.hasOwnProperty.call(devicesObj, deviceId)) {
        nextDevices[retailerName][branchName][deviceId] = {
          ...devicesObj[deviceId],
          notes,
        };
        updatedCount += 1;
      }
    });
  });

  return { nextDevices, updatedCount };
};

export const patchMultipleDeviceNotesInCache = (source, deviceIds, notes) => {
  const normalizedDeviceIds = [
    ...new Set(
      (deviceIds || []).map((deviceId) => String(deviceId || "").trim()).filter(Boolean)
    ),
  ];
  let nextDevices = source || {};
  let updatedCount = 0;

  normalizedDeviceIds.forEach((deviceId) => {
    const { nextDevices: patchedDevices, updatedCount: patchedCount } = patchDeviceNotesInCache(
      nextDevices,
      deviceId,
      notes
    );
    nextDevices = patchedDevices;
    updatedCount += patchedCount;
  });

  return { nextDevices, updatedCount };
};
